import {
  Box,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import { RouteComponentProps } from '@reach/router';
import { Button, ReadMoreLess, TextView } from '@atoms';
import { useCustomValues, callSyncLocation, useBillingAccounts } from '@api';
import EditNoteRounded from '@mui/icons-material/EditNoteRounded';
import UpdateCustomValuesModal from './update-custom-values';
import { BillingAccountSelector } from '@organisms';

const windowInnerHeight = window.innerHeight;

const CustomValues: React.FC<RouteComponentProps> = () => {
  const [accounts, setAccounts] = useState<BillingAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<BillingAccount>();
  const [updateValues, setUpdateValues] = useState<CustomValues>();
  const [itemIndex, setIndex] = useState<number>(0);
  const [synclocation, setSyncLocation] = useState<boolean>(false);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);

  const {
    data: customValues,
    refetch: refetchCustomValues,
    isFetching: isLoadingCustomValues
  } = useCustomValues(selectedAccount?.locationId, true);

  const { data: response, isFetching } = useBillingAccounts({
    type: 'c',
    status: '',
    caller: 'custom-values'
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = response?.data;
        const code = response?.code;
        if (code === 200 && data) {
          const sortedData = data.data.sort((a, b) => a.companyName.localeCompare(b.companyName));

          setAccounts(sortedData);

          if (!selectedAccount && sortedData.length > 0) {
            setSelectedAccount(sortedData[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [response]);

  const handleAccountClick = (item: BillingAccount) => {
    setSelectedAccount(item);
  };
  const handleCustomValues = (item: CustomValues, index: number) => {
    setUpdateValues(item);

    setIndex(index);
  };
  const handleNext = () => {
    const foundValue = customValues && customValues.data?.find((value, index) => index > itemIndex);

    if (foundValue) {
      setUpdateValues(foundValue);
      setIndex(itemIndex + 1);
    }
  };

  const handlePreview = () => {
    let lastValue = null;
    const length = customValues?.data?.length || 0;
    for (let index = 0; customValues && index < length && index < itemIndex; index++) {
      const value = customValues.data?.[index];

      lastValue = value;
    }

    if (lastValue) {
      setUpdateValues(lastValue);
      setIndex(itemIndex - 1);
    }
  };

  const syncLocation = () => {
    setSyncLocation(true);
    callSyncLocation(selectedAccounts)
      .then((res) => {
        if (res.code == 200) {
          setSyncLocation(false);
        }
      })
      .catch((error) => {
        console.error('Error syncing location:', error instanceof Error ? error.message : error);
      });
  };

  return (
    <>
      <Box m={4}>
        <Box sx={{ mt: 1, mb: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Box/>
          <Stack direction={'row'}>
            <Stack direction={'row'} ml={2} mr={2} alignItems={'center'}>
              <BillingAccountSelector
                variant="checkbox-list"
                multiple
                type="c"
                status="both"
                caller="custom-values-sync"
                defaultSelectAll
                onSelected={(accounts) =>
                  setSelectedAccounts(
                    accounts.map((a) => a.locationId).filter((v): v is string => !!v)
                  )
                }
              />
            </Stack>
            <Button size="large" loading={synclocation} onClick={syncLocation}>
              Sync Location
            </Button>
          </Stack>
        </Box>
        <Grid container mt={3} bgcolor={'#F6F5F5'} borderRadius={'16px'}>
          <Grid item xs={3} sm={4} md={4} lg={4} xl={3}>
            <nav
              style={{
                height: windowInnerHeight - 20, // Adjust as needed
                overflowY: 'auto',
                backgroundColor: '#E2E5E6',
                paddingRight: '10px',
                paddingLeft: '10px',
                borderRadius: '16px'
              }}>
              <List sx={{ height: '100%' }}>
                {accounts?.map((item) => (
                  <ListItem
                    key={item.companyName}
                    component="div"
                    selected={selectedAccount === item}
                    className={selectedAccount === item ? 'selected' : ''}
                    style={{
                      backgroundColor: selectedAccount === item ? '#086BDE' : 'inherit',
                      color: selectedAccount === item ? 'white' : 'inherit',
                      borderRadius: selectedAccount === item ? '10px' : '0px'
                    }}
                    onClick={() => handleAccountClick(item)}>
                    <ListItemText
                      primary={item.companyName}
                      primaryTypographyProps={{
                        fontSize: 16,
                        fontWeight: 600
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </nav>
          </Grid>
          <Grid item xs={9} sm={8} md={8} lg={8} xl={9} spacing={2}>
            <nav
              style={{
                height: windowInnerHeight - 20, // Adjust as needed
                overflowY: 'auto'
              }}>
              {isFetching || isLoadingCustomValues ? (
                <>
                  {customValues?.data?.map((item, index) => (
                    <ListItem key={index}>
                      <ListItemText>
                        <TextView variant="h5">
                          <Skeleton animation="wave" />
                        </TextView>
                      </ListItemText>
                    </ListItem>
                  ))}
                </>
              ) : (
                <List>
                  {customValues?.data?.map((item, index) => (
                    <ListItem key={item.fieldKey}>
                      <ListItemText
                        primary={item.name}
                        secondary={<ReadMoreLess text={item?.value} maxLength={455} />}
                        primaryTypographyProps={{
                          fontSize: 18,
                          fontWeight: 550
                        }}
                        secondaryTypographyProps={{
                          fontSize: 14,
                          fontFamily: 'Arial',
                          fontWeight: 400
                        }}
                      />

                      <ListItemIcon>
                        <EditNoteRounded
                          onClick={() => {
                            handleCustomValues(item, index);
                            refetchCustomValues();
                          }}
                        />
                      </ListItemIcon>
                    </ListItem>
                  ))}
                </List>
              )}
              <UpdateCustomValuesModal
                initialValues={updateValues}
                status={''}
                onClose={() => {
                  setUpdateValues(undefined);
                  refetchCustomValues();
                }}
                handleNextindex={() => {
                  handleNext();
                }}
                handlePreview={() => {
                  handlePreview();
                }}
              />
            </nav>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default CustomValues;
